/**
 * IncomingCallModal — full-screen "someone is calling you" modal.
 *
 * Mounted globally in <DoserneShell />. Listens for:
 *   1. `doserne:incoming_call` custom events (dispatched by the SW
 *      when a chat push of type "incoming_call" arrives).
 *   2. URL param `?incoming_call=<call_id>` on mount (fallback when
 *      the SW opens a fresh window from a locked-screen tap).
 *
 * On Answer → mount <CallOverlay direction="incoming" callId=... />.
 * On Decline → POST /calls/decline and dismiss.
 *
 * Plays a short ringtone via HTMLAudioElement while active — Android
 * PWA in foreground can loop this until answered. On iOS, the OS
 * notification sound already played; the loop here is silent unless
 * the app is already open.
 */
import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import axios from "axios";
import { Phone, PhoneOff, Video as VideoIcon } from "lucide-react";
const CallOverlay = lazy(() => import("./CallOverlay"));
import { useRingtone } from "./useRingtone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IncomingCallModal({ authHeader, user }) {
  const [incoming, setIncoming] = useState(null); // {call_id, mode, caller_name, ...}
  const [answered, setAnswered] = useState(false);

  // Messenger-style ring while the modal is visible + not yet answered.
  useRingtone(incoming && !answered ? "incoming" : null);

  // Listen for events from the SW bridge.
  useEffect(() => {
    const onEvent = (evt) => {
      const d = evt?.detail;
      if (!d?.call_id) return;
      setIncoming(d);
      setAnswered(false);
    };
    window.addEventListener("doserne:incoming_call", onEvent);
    // SW postMessage bridge — same as the chat-sheet one.
    const onSw = (evt) => {
      const d = evt.data;
      if (d?.type === "DOSERNE_INCOMING_CALL" && d?.call_id) {
        setIncoming(d);
        setAnswered(false);
      }
      // The call was answered elsewhere or cancelled by the caller —
      // instantly close our modal + stop the ringtone.
      if (d?.type === "DOSERNE_CALL_CANCELLED" && d?.call_id) {
        setIncoming((cur) => (cur && cur.call_id === d.call_id && !cur.__answered) ? null : cur);
      }
    };
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onSw);
    }
    // URL param fallback when SW opens a fresh window.
    try {
      const url = new URL(window.location.href);
      const cid = url.searchParams.get("incoming_call");
      if (cid) {
        url.searchParams.delete("incoming_call");
        window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") + url.hash);
        setIncoming({ call_id: cid, mode: "audio", caller_name: "Doserne" });
      }
    } catch { /* ignore */ }

    // Reliability net: poll /calls/incoming every 4 s while the tab
    // is visible. Catches the case where SW postMessage silently
    // fails (Chrome cache issues, iOS quirks) — we won't miss a call.
    // Also: if a call is open on this tab but no longer in the ringing
    // list (answered on another device, cancelled, timed out) we close
    // the modal here — fixes the "desktop keeps ringing after phone
    // picks up" bug.
    let cancelled = false;
    const poll = async () => {
      if (cancelled || document.visibilityState === "hidden") return;
      try {
        const { data } = await axios.get(`${API}/doserne/calls/incoming`, authHeader());
        const ringing = data.calls || [];
        const row = ringing[0];
        setIncoming((cur) => {
          // 1) A ringing call was found → open modal for it (unless
          //    it's the same one already declined/answered locally).
          if (row?.id) {
            if (cur && cur.call_id === row.id) return cur;
            if (cur && (cur.__declined || cur.__answered)) return cur;
            return {
              call_id: row.id,
              chat_room_id: row.chat_room_id,
              mode: row.mode,
              caller_name: row.caller_name,
              caller_email: row.caller_email,
              lk_room: row.lk_room,
            };
          }
          // 2) No ringing calls anymore → close ANY open incoming
          //    modal on this device (it was answered elsewhere,
          //    caller cancelled, or the ring window expired).
          if (cur && !cur.__answered) {
            return null;
          }
          return cur;
        });
      } catch { /* silent */ }
    };
    const pollTimer = setInterval(poll, 4000);
    poll();
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("doserne:incoming_call", onEvent);
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onSw);
      }
    };
  }, [authHeader]);

  const decline = async () => {
    if (!incoming) return;
    try {
      await axios.post(`${API}/doserne/calls/decline`, { call_id: incoming.call_id }, authHeader());
    } catch { /* ignore */ }
    // Keep a stub around briefly so the poller doesn't immediately re-open the modal.
    setIncoming({ ...incoming, __declined: true });
    setTimeout(() => setIncoming(null), 5000);
  };

  const answer = () => {
    setIncoming((cur) => cur ? { ...cur, __answered: true } : cur);
    setAnswered(true);
  };

  const closeAll = () => {
    setIncoming(null);
    setAnswered(false);
  };

  const caller = useMemo(() => incoming?.caller_name || "Doserne", [incoming]);

  if (!incoming || incoming.__declined) return null;

  if (answered) {
    return (
      <Suspense fallback={null}>
        <CallOverlay
          direction="incoming"
          mode={incoming.mode || "audio"}
          chatRoomId={incoming.chat_room_id || ""}
          callId={incoming.call_id}
          peerLabel={caller}
          authHeader={authHeader}
          onClose={closeAll}
        />
      </Suspense>
    );
  }

  const initials = (caller || "?").slice(0, 1).toUpperCase();

  return (
    <div className="fixed inset-0 z-[80] bg-gradient-to-b from-slate-900 to-slate-950 flex flex-col items-center justify-between p-6" data-testid="incoming-call-modal" style={{ color: "#fff" }}>
      <div className="pt-[calc(env(safe-area-inset-top,0px)+16px)] text-center">
        <div className="text-xs uppercase tracking-widest opacity-70 font-black" style={{ color: "#fff" }}>
          {incoming.mode === "video" ? "Innkommende videosamtale" : "Innkommende samtale"}
        </div>
        <div className="text-2xl font-black mt-1" style={{ color: "#fff" }}>{caller}</div>
        <div className="text-sm mt-1 opacity-80" style={{ color: "#fff" }}>Doserne Academy</div>
      </div>

      <div className="w-48 h-48 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center shadow-2xl animate-pulse">
        <span className="text-7xl font-black" style={{ color: "#fff" }}>{initials}</span>
      </div>

      <div className="w-full pb-[calc(env(safe-area-inset-bottom,0px)+16px)] flex items-center justify-center gap-16">
        <button
          onClick={decline}
          className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-xl active:scale-95"
          data-testid="incoming-decline-btn"
          aria-label="Avvis"
        >
          <PhoneOff className="w-7 h-7" style={{ color: "#fff" }} />
        </button>
        <button
          onClick={answer}
          className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center shadow-xl active:scale-95 animate-bounce"
          data-testid="incoming-answer-btn"
          aria-label="Svar"
        >
          {incoming.mode === "video" ? <VideoIcon className="w-7 h-7" style={{ color: "#fff" }} /> : <Phone className="w-7 h-7" style={{ color: "#fff" }} />}
        </button>
      </div>
    </div>
  );
}
